import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { toast } from "react-toastify";
import { KintoneAdminSettings } from "~/components/KintoneAdmin/KintoneAdminSettings";
import Knowledges from "~/components/Project/knowledge";
import OnProjectCreatedModal from "~/components/Project/onProjectCreatedModal";
import ProjectMemberContent from "~/components/Project/projectMemberContent";
import SelectionModal from "~/components/Summary/importSelectionModal";
import CreditLackModal from "~/components/ui/creditLackModal";
import PageHeader from "~/components/ui/pageHeader";
import { azureLoginUri } from "~/config/apiConfig";
import { DOCUMENT_TYPES } from "~/config/documents";
import { TOAST_MESSAGES } from "~/constants/messages";
import { PageView } from "~/features/projectTop/pageView";
import ProjectTopListContent from "~/features/projectTop/projectTopListContent";
import { useWebPubSub } from "~/features/projectTop/webPubSubContext";
import { GenerateApi } from "~/functions/api/generate";
import { ProjectApi } from "~/functions/api/project";
import { useGenerationFallback } from "~/hooks/useGenerationFallback";
import { usePendingKnowledgeCount } from "~/hooks/usePendingKnowledgeCount";
import { useWebPubSubMessageHandler } from "~/hooks/useWebPubSubMessageHandler";
import { setProjectMembers } from "~/slices/auth";
import { clearError, fetchProjectData } from "~/slices/projectSlice";
import type { AppDispatch, RootState } from "~/store";
import { connect, createClient, disconnect } from "~/utils/webPubSubUtils";
import styles from "./index.module.scss";

const ProjectTop = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentProject, currentUser, organization } = useSelector(
    (state: RootState) => state.auth,
  );
  const { isLoading, error } = useSelector((state: RootState) => state.project);
  const [isNewlyCreated, setIsNewlyCreated] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isSummaryCreditLackModalOpen, setIsSummaryCreditLackModalOpen] = useState(false);
  const [summaryLackCredit, setSummaryLackCredit] = useState(0);
  // WebPubSub メッセージハンドラを全ビュー共通で登録
  useWebPubSubMessageHandler();
  useGenerationFallback();

  // カスタムフックでpending knowledgeカウントを管理
  const { count: pendingKnowledgeCount } = usePendingKnowledgeCount();

  const navigate = useNavigate();
  const currentLocation = useLocation();
  const { client, setClient } = useWebPubSub();

  // projectIdとorganizationIdを取得
  const projectId = currentProject?.id;
  const organizationId = currentUser?.organization_id;

  useEffect(() => {
    const pathSegments = currentLocation.pathname.split("/");
    const lastSegment = pathSegments[pathSegments.length - 1];
    const index = ["dashboard", "info", "members", "knowledge", "settings"].indexOf(
      lastSegment || "",
    );
    if (index !== -1) {
      setActiveTab(index);
    }
  }, [currentLocation.pathname]);

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    const tabPaths = ["dashboard", "info", "members", "knowledge", "settings"];
    navigate(`/${currentUser.organization_id}/project/${currentProject!.id}/${tabPaths[index]!}`);
  };

  const handleOpenSummaryModal = () => {
    if (organization) {
      const creditCost = DOCUMENT_TYPES.summary.creditUsage.generate;
      if (organization.remainingCredit < creditCost) {
        setSummaryLackCredit(creditCost - organization.remainingCredit);
        setIsSummaryCreditLackModalOpen(true);
        return;
      }
    }
    setIsSummaryModalOpen(true);
  };

  const handleCloneOnProjectCreated = async () => {
    setIsNewlyCreated(false);
    searchParams.delete("new");
    setSearchParams(searchParams);
  };

  useEffect(() => {
    const isNew = searchParams.get("new");
    if (isNew === "true") {
      setIsNewlyCreated(true);
    }
  }, [searchParams]);

  // WebPubSub: connect once per organization, avoid reconnect loops
  const lastOrgIdRef = useRef<string | null>(null);
  useEffect(() => {
    const setup = async () => {
      if (!organization?.id) return;
      // Already connected for this org
      if (client && lastOrgIdRef.current === organization.id) return;
      // Connected for different org -> disconnect first
      if (client && lastOrgIdRef.current && lastOrgIdRef.current !== organization.id) {
        await disconnect(client);
        setClient(null);
      }
      if (!client) {
        const generateApi = new GenerateApi();
        const response = await generateApi.getClientAccessUrl(organizationId!);
        if (response !== null) {
          const url = response;
          const webPubSubClient = await createClient(url);
          if (webPubSubClient !== null) {
            await connect(webPubSubClient);
            // 自動再接続のリトライ上限到達時にクライアントを再作成する
            webPubSubClient.on("stopped", () => {
              lastOrgIdRef.current = null;
              setClient(null);
            });
            // 接続完了後にsetClientを呼ぶことで、他のコンポーネントは接続済みのclientのみ取得可能
            setClient(webPubSubClient);
          }
          lastOrgIdRef.current = organization.id;
        } else {
          toast.error(TOAST_MESSAGES.PROJECT.RESELECT_PROJECT);
          navigate(`/${organization.id}/home`);
        }
      }
    };
    setup();
  }, [organization?.id, organizationId, client, setClient, navigate]);

  // Fetch project-related data once project/org IDs are available
  useEffect(() => {
    if (!currentProject?.id || !organization?.id) return;
    dispatch(fetchProjectData());
  }, [currentProject?.id, organization?.id, dispatch]);

  // projectId変更時にメンバーを取得（Sidebarでの二重取得を回避）
  useEffect(() => {
    const fetchMembers = async () => {
      if (!currentProject?.id || !currentUser?.organization_id) return;
      const api = new ProjectApi();
      const members = await api.getMembers(currentProject.id, currentUser.organization_id);
      if (members) {
        dispatch(setProjectMembers(members));
      }
    };
    fetchMembers();
  }, [currentProject?.id, currentUser?.organization_id, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      if (error === "再ログインしてください") {
        window.location.href = azureLoginUri;
      }
      dispatch(clearError());
    }
  }, [error, dispatch]);

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  if (!currentProject && projectId && organizationId) {
    return <div>ロード中...</div>;
  }

  if (!currentProject) {
    return <div>プロジェクトが見つかりません。</div>;
  }

  // サンプルデータ
  const tabTitles = ["ダッシュボード", "詳細情報", `メンバー`, "ナレッジ", "設定"];
  const tabIds = ["dashboard", "info", "members", "knowledge", "settings"];

  return (
    <div>
      {isNewlyCreated && (
        <OnProjectCreatedModal
          label={"プロジェクトが作成されました🎉"}
          isOpen={isNewlyCreated}
          onClose={handleCloneOnProjectCreated}
        />
      )}
      <PageHeader title={currentProject.name} />
      <Tabs className={styles["tab"]} onSelect={handleTabChange} selectedIndex={activeTab}>
        <TabList className={styles["tab-list"]}>
          {tabTitles.map((title, index) => (
            <Tab
              key={tabIds[index]}
              className={styles["tab-list-name"]}
              selectedClassName={styles["tab-list-name--selected"]}
            >
              <span>{title}</span>
              {index === 3 && pendingKnowledgeCount > 0 && (
                <span className={styles["knowledge-badge"]}>{pendingKnowledgeCount}</span>
              )}
            </Tab>
          ))}
        </TabList>

        <TabPanel key={tabIds[0]!} className={styles["custom-tab-panel"]}>
          <PageView
            organizationId={organizationId || ""}
            projectId={projectId || ""}
            onOpenSummaryModal={handleOpenSummaryModal}
          />
        </TabPanel>
        <TabPanel key={tabIds[1]!} className={styles["custom-tab-panel"]}>
          <ProjectTopListContent />
        </TabPanel>
        <TabPanel key={tabIds[2]!} className={styles["custom-tab-panel"]}>
          <ProjectMemberContent />
        </TabPanel>
        <TabPanel key={tabIds[3]!} className={styles["custom-tab-panel"]}>
          <Knowledges />
        </TabPanel>
        <TabPanel key={tabIds[4]!} className={styles["custom-tab-panel"]}>
          {currentUser?.organization_id && currentProject?.id && (
            <KintoneAdminSettings
              organizationId={currentUser.organization_id}
              projectId={currentProject.id}
            />
          )}
        </TabPanel>
      </Tabs>
      <div className={styles["floatingButtons"]}>
        <button
          type="button"
          className={styles["floatingDashboardButton"]}
          onClick={() => {
            if (!currentUser.organization_id || !currentProject?.id) return;
            navigate(
              `/${currentUser.organization_id}/project/${currentProject.id}/generate-status`,
            );
          }}
        >
          他のドキュメントを生成する
        </button>
        <button
          type="button"
          className={styles["scrollTopButton"]}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="ページの一番上へ"
        >
          <ArrowUpwardIcon />
        </button>
      </div>
      {isSummaryModalOpen && <SelectionModal onClose={() => setIsSummaryModalOpen(false)} />}
      {isSummaryCreditLackModalOpen && (
        <CreditLackModal
          isOpen={isSummaryCreditLackModalOpen}
          onClose={() => setIsSummaryCreditLackModalOpen(false)}
          lackCredit={summaryLackCredit}
        />
      )}
    </div>
  );
};

export default ProjectTop;
